import React, { FunctionComponent, useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { Form, Formik, FormikProps, FormikErrors } from 'formik';
import { IBindingCallback1, IBindingAction } from '../../../../common/models/callback';
import { ICreateUser } from '../../../../common/models/user/ICreateUser';
import { ISaveUserError } from '../../../../common/models/user/ISaveUserError';
import { IAppState } from '../../../../common/models/store/IAppState';
import { createUserRoutine, setCreateUserErrorRoutine } from '../../routines';
import LoaderWrapper from '../../../../components/LoaderWrapper';
import { createUserValidationSchema } from '../../../../common/validation/yup';
import { ITeam } from '../../../../common/models/team/ITeam';
import { RoleType } from '../../../../common/enums/RoleType';
import RegularButton from '../../../../components/RegularButton';
import CustomSelect from '../../../../components/CustomSelect';
import { getRoleNodes } from '../../../../common/helpers/nodesHelper';
import CustomPopover from '../../../../components/CustomPopover';
import TeamList from '../../../../containers/TeamList';
import Icon from '../../../../components/Icon';
import Input from '../../../../components/Input';
import { concat } from '../../../../common/helpers/arrayHelper';
import styles from './styles.module.scss';

interface IProps {
  saveError: ISaveUserError | null;
  isSaving: boolean;
  createUser: IBindingCallback1<ICreateUser>;
  resetSaveError: IBindingCallback1<null>;
}

interface ICreateUserData {
  firstName: string;
  lastName: string;
  email: string;
  role: RoleType;
  team?: ITeam;
}

const CreateUserForm: FunctionComponent<IProps> = ({ saveError, isSaving, createUser, resetSaveError }) => {
  const defaultUserData: ICreateUserData = {
    firstName: '',
    lastName: '',
    email: '',
    role: '' as RoleType
  };

  const [isSubmitting, setIsSubmitting] = useState(false);

  const onSubmit = ({ team, ...values }: typeof defaultUserData) => {
    const data: ICreateUser = {
      teamId: team?.id as string,
      ...(values as ICreateUserData)
    };
    createUser(data);
  };

  const onAddClick = (submit: IBindingAction) => {
    setIsSubmitting(true);
    submit();
  };

  const TeamListComponent = useCallback(({ value, onChange }) => (
    <TeamList
      advanced
      selectedIds={value ? [value] : undefined}
      onSelect={([data]) => onChange(data)}
    />
  ), []);

  const getError = (fieldName: keyof ICreateUserData, errors: FormikErrors<ICreateUserData>) => (
    isSubmitting ? errors[fieldName] : undefined
  );

  const emailError = saveError?.isEmailUsed && saveError.message;

  useEffect(() => () => resetSaveError(null), []);

  return (
    <LoaderWrapper loading={isSaving} transparent>
      <Formik
        validationSchema={createUserValidationSchema}
        initialValues={defaultUserData}
        onSubmit={onSubmit}
        validateOnMount
      >
        {({
          isValid,
          submitForm,
          values,
          setFieldValue,
          errors
        }: FormikProps<ICreateUserData>) => (
          <Form className={styles.formContainer}>
            <header className="custom-modal-header">
              <FormattedMessage id="usersPane.createNewUser" defaultMessage="Create New User" />
            </header>
            <main className={styles.createUserForm}>
              <Input
                label={{ id: 'firstName', defaultMessage: 'First Name' }}
                onChange={val => setFieldValue('firstName', val)}
                errorIntl={getError('firstName', errors)}
                variant="underlined"
                value={values.firstName}
                className={styles.inputContainer}
              />
              <Input
                label={{ id: 'lastName', defaultMessage: 'Last Name' }}
                onChange={val => setFieldValue('lastName', val)}
                errorIntl={getError('lastName', errors)}
                variant="underlined"
                value={values.lastName}
                className={styles.inputContainer}
              />
              <Input
                label={{ id: 'emailAddress', defaultMessage: 'Email Address' }}
                errorIntl={emailError || getError('email', errors)}
                onChange={val => {
                  if (emailError) resetSaveError(null);
                  setFieldValue('email', val);
                }}
                type="email"
                value={values.email}
                variant="underlined"
                className={styles.inputContainer}
              />
              <div className={`d-flex align-items-end ${styles.selectBlock}`}>
                <div className={concat('popup-wrapper',
                  styles.teamDropdownWrp,
                  'align-self-stretch',
                  Boolean(getError('team', errors)) && styles.errorSelect)}
                >
                  <CustomPopover
                    placement="top-end"
                    popoverClassName={styles.chooseTeamPopover}
                    trigger={(
                      <div className="d-flex justify-content-between text-dropdown-trigger">
                        <span className="overflow-ellipsis">
                          {values.team?.name
                          || (
                            <span className="text-dropdown-trigger-placeholder">
                              <FormattedMessage id="team" defaultMessage="Team" />
                            </span>
                          )}
                        </span>
                        <Icon icon="chevronDown" className="popup-chevron" />
                      </div>
                    )}
                    rootClose
                  >
                    <TeamListComponent onChange={(val: ITeam) => setFieldValue('team', val)} value={values.team?.id} />
                  </CustomPopover>
                </div>
                <div className={concat(styles.roleDropdown, Boolean(getError('role', errors)) && styles.errorSelect)}>
                  <CustomSelect
                    value={values.role}
                    onChange={role => setFieldValue('role', role)}
                    options={getRoleNodes()}
                    defaultValueIntl="role"
                    defaultValueText="Role"
                    variant="radio"
                    triggerVariant="underlined"
                    placement="top-end"
                  />
                </div>
              </div>
            </main>
            <footer className="modal-footer-bttns">
              <RegularButton
                enableAction={!emailError}
                onClick={() => onAddClick(submitForm)}
                textIntlId="usersPane.addUser"
                defaultMessage="+ Add User"
                disabled={!isValid || Boolean(emailError)}
              />
            </footer>
          </Form>
        )}
      </Formik>
    </LoaderWrapper>
  );
};

const mapStateToProps = ({
  settings: {
    usersAndTeams: {
      isSaving, saveError
    }
  }
}: IAppState) => ({ isSaving, saveError });

const mapDispachToProps = {
  createUser: createUserRoutine,
  resetSaveError: setCreateUserErrorRoutine
};

export default connect(mapStateToProps, mapDispachToProps)(CreateUserForm);
